import { Form, Formik } from "formik";
import * as Yup from "yup";

import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import TextField from "@components/Forms/FormInputs/TextField";

import { IPortfolioTechnology } from "@interfaces/portfolio.type";

import { usePortfolioQuery } from "@queries/portfolio.query";

interface IAddToPortfolioProperties {
  createTechnologySubmit: (name: string, description: string) => Promise<void>;
}

export default function AddTechnologyToPortfolio({ createTechnologySubmit }: IAddToPortfolioProperties) {
  const { data: portfolios, status } = usePortfolioQuery();
  const existingNames: string[] = [];
  if (portfolios) {
    let existingPortfolios;
    if ("portfolio" in portfolios && Array.isArray(portfolios.portfolio)) {
      existingPortfolios = portfolios.portfolio;
    } else if (Array.isArray(portfolios)) {
      existingPortfolios = portfolios;
    } else {
      throw new TypeError("Invalid portfolios structure");
    }
  }

  const createTechnologySchema = Yup.object().shape({
    name: Yup.string().required("Required").notOneOf(existingNames, "Technology with this name already exists."),
  });

  return (
    <Box sx={{ width: "700px" }}>
      <Formik
        initialValues={{
          description: "",
          name: "",
        }}
        onSubmit={async (values) => {
          await createTechnologySubmit(values.name, values.description);
        }}
        validationSchema={createTechnologySchema}
      >
        {({ dirty, isSubmitting, isValid }) => (
          <Box
            component={Form}
            display="flex"
            flexDirection="column"
            sx={{
              gap: 1,
              width: "100%", // Fix IE 11 issue.
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: "bold" }} variant="body2">
                Technology Name *
              </Typography>
              <TextField
                color="secondary"
                inputProperties={{ maxLength: "90", minLength: "1" }}
                isFullWidth
                isRequired
                label=""
                name="name"
                size="medium"
                type="text"
              />
            </Box>

            <Box sx={{ bottom: "5px", position: "relative" }}>
              <Box sx={{ position: "relative", right: "8px", top: "8px" }}>
                <Tooltip title="Add a technology description to quickly perform a SmartSearch and more.">
                  <IconButton>
                    <InfoIcon sx={{ color: "#0288D1", fontSize: "18px" }} />
                  </IconButton>
                </Tooltip>

                <Typography
                  sx={{ display: "inline-block", fontSize: 12, fontWeight: "bold", position: "relative", right: "4px" }}
                  variant="body2"
                >
                  Technology Description
                </Typography>
              </Box>

              <TextField
                color="secondary"
                isFullWidth
                isMultiline={true}
                label=""
                name="description"
                rows={10}
                size="medium"
                type="text"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", gap: 1, p: 0, width: "100%" }}>
                <Button
                  disabled={!isValid || !dirty || isSubmitting || status !== "success"}
                  startIcon={<SaveIcon />}
                  sx={{
                    borderRadius: "8px",
                  }}
                  type="submit"
                  variant="contained"
                >
                  {isSubmitting ? <CircularProgress size={20} /> : "Create Technology"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Formik>
    </Box>
  );
}
